import { useAtomValue } from 'jotai';
import type { ColorValue } from 'react-native';
import {
    Card,
    type CardProps,
    ScrollView,
    SizableText,
    XStack,
    YStack,
} from 'tamagui';

import { H6Regular } from '../../components/text/Typography';
import {
    type ColorPalette,
    colorTokens,
} from '../../themes/SmoothieColorTokens';
import { activeThemeAtom } from '../BlenderAtoms';

const colorToFruit = {
    brand: '🥭',
    gray: '🥥',
    green: '🍏',
    orange: '🍊',
    red: '🍓',
    tomatoRed: '🍅',
    peach: '🍑',
    marigold: '🌼',
    wasabi: '🍱',
    dropletBlue: '💧',
    sweetViolet: '🍇',
    orchidPink: '🌸',
};
const sortOrder = Object.keys(colorToFruit);

export function Colors() {
    const activeTheme = useAtomValue(activeThemeAtom);
    const colorPalettes = colorTokens[activeTheme ?? 'light'];
    const sortedColorPalettes = Object.entries(colorPalettes).sort(
        ([colorA], [colorB]) =>
            sortOrder.indexOf(colorA) - sortOrder.indexOf(colorB),
    );

    return (
        <YStack gap="$3" maxWidth="100%">
            {sortedColorPalettes.map(([colorPaletteName, colorPalette]) => {
                return (
                    <ColorPaletteDemo
                        key={colorPaletteName}
                        name={`${colorToFruit[colorPaletteName] ?? ''} ${colorPaletteName}`}
                        colorPalette={colorPalette}
                    />
                );
            })}
        </YStack>
    );
}

interface ColorDemoPaletteProps {
    name: string;
    colorPalette: ColorPalette;
}

function ColorPaletteDemo({ name, colorPalette }: ColorDemoPaletteProps) {
    return (
        <YStack paddingHorizontal="$3">
            <H6Regular>{name}</H6Regular>
            <ScrollView horizontal={true} maxHeight={150} scrollbarWidth="none">
                <XStack gap={2} marginVertical="$3">
                    {Object.entries(colorPalette).map(
                        ([colorShadeName, colorValue]) => {
                            return (
                                <ColorCard
                                    key={colorShadeName}
                                    name={colorShadeName}
                                    value={colorValue}
                                    animation="button"
                                    width={116}
                                    height={116}
                                    hoverStyle={{ scale: 1.07 }}
                                />
                            );
                        },
                    )}
                </XStack>
            </ScrollView>
        </YStack>
    );
}

interface ColorCardProps extends CardProps {
    name: string;
    value: ColorValue;
}

function ColorCard({ name, value, ...props }: ColorCardProps) {
    return (
        <Card backgroundColor={value} bordered {...props}>
            <Card.Footer padding="$1.5" backgroundColor="$background">
                <YStack gap={4}>
                    <SizableText size="$1" fontWeight={700}>
                        {name}
                    </SizableText>
                    <SizableText size="$1" color="$gray1100">
                        {value.toString().toUpperCase()}
                    </SizableText>
                </YStack>
            </Card.Footer>
        </Card>
    );
}
